<template>
  <v-container fluid class="ma-4 px-8">
    <v-row>
      <v-col cols="12" md="4" class="py-0 my-0">
        <v-autocomplete
          v-model="form.particular_ids"
          :items="addonTypes"
          outlined
          dense
          multiple
          chips
          hide-details="auto"
          label="Add-on Programmes"
          item-text="text"
          item-value="id"
          @change="reportData = { fee: [], current_date: null }"
          clearable
        >
          <template v-slot:selection="{ item, index }">
            <div class="d-flex align-center">
              <v-chip
                v-if="index < 1"
                small
                class="mr-2"
                style="max-width: 140px !important; margin: 2px"
                outlined
              >
                {{ item.text }}
              </v-chip>
              <span v-if="index === 1" class="text-caption ml-2">
                (+{{ form.particular_ids.length - 1 }} others)
              </span> 
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" md="2" class="py-0 my-0">
        <v-autocomplete
          v-model="form.year_batch"
          :items="batches"
          outlined
          dense
          hide-details="auto"
          label="Batch"
          clearable
        ></v-autocomplete>
      </v-col>

      <v-col cols="12" md="4" class="py-0 my-0">
        <v-btn
          class="accent white--text"
          @click="fetchReport()"
          :loading="loading"
        >
          Search
        </v-btn>
        <v-btn class="accent white--text ml-2" @click="download" :loading="downloadLoading">
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="reportData?.fee?.length" class="mt-8">
      <v-col cols="12" class="text-center py-0">
        <span class="text-5xl font-weight-bold accent--text">
          {{ selectedFeeNames }} Collection Status as on
          {{ reportData.current_date }}
        </span>
      </v-col>
      
      <v-col cols="12" class="d-flex justify-center">
        <v-sheet class="pa-4" style="overflow-x: auto">
          <table border="1" cellpadding="12" cellspacing="0" class="text-center" style="border-collapse: collapse; min-width: 600px">
            <thead>
              <tr style="background-color: #f5f5f5">
                <th class="px-4 py-2" style="min-width: 150px">Programme</th>
                <th class="px-4 py-2" style="min-width: 100px">Fee Raised Count</th>
                <th class="px-4 py-2" style="min-width: 120px">Fee Raised Value (₹)</th>
                <th class="px-4 py-2" style="min-width: 100px">Fee Received Count</th>
                <th class="px-4 py-2" style="min-width: 120px">Fee Received Value (₹)</th>
                <th class="px-4 py-2" style="min-width: 80px">Received %</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in reportData.fee"
                :key="index"
                :class="{ 'highlight-row': isTotalRow(item.program_name) }"
              >
                <th
                  class="px-4 py-2"
                  :class="{ 'right-align-row': isTotalRow(item.program_name) }"
                >
                  {{ item.program_name }}
                </th>
                <td class="px-4 py-2">{{ formatValue(item.fee_raised_count) }}</td>
                <td class="px-4 py-2">{{ formatValue(item.fee_raised_value, true) }}</td>
                <td class="px-4 py-2">{{ formatValue(item.fee_received_count) }}</td>
                <td class="px-4 py-2">{{ formatValue(item.fee_received_value, true) }}</td>
                <td class="px-4 py-2">{{ formatPercentage(item.received_percentage) }}</td>
              </tr>
            </tbody>
          </table>
        </v-sheet>
      </v-col>
    </v-row>

    <v-row v-if="!reportData?.fee?.length && !loading" align="center" class="px-4">
      <v-col cols="12" class="d-flex justify-center">
        <span class="text-h6">No Data Found</span>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      form: {
        particular_ids: [],
        status: null,
      },
      reportData: { fee: [], current_date: null },
      addonTypes: [],
      loading: false,
      downloadLoading: false,
      statusOptions: [
        { text: "All", value: null },
        { text: "Completed", value: "Completed" },
        { text: "Pending", value: "Pending" },
      ],
      batches: [],
    };
  },
  computed: {
    selectedFeeNames() {
      if (!this.form.particular_ids || !this.form.particular_ids.length) return "All Addons";
      const selected = this.addonTypes
        .filter((f) => this.form.particular_ids.includes(f.id))
        .map((f) => f.text);
      return selected.length ? selected.join(", ") : "All Addons";
    },
  },
  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Add-on Programme Report" }]);
    await this.getAddons();
    await this.fetchReport();
    await this.generateBatchList();
  },
  methods: {
    async getAddons() {
      try {
        const res = await this.$axios.get("/program_addons");
        const addons = res.data.data;
        this.addonTypes = addons
          .sort((a, b) => a.id - b.id)
          .map((f) => ({ id: f.id, text: f.name }));
      } catch (error) {
        console.log(error);
      }
    },
    
    isTotalRow(programName) {
      return programName && programName.includes("Total");
    },
    
    formatValue(value, amount = false) {
      if (value === undefined || value === null) return amount ? "0" : 0;
      
      let num;
      if (typeof value === 'string') {
        num = parseFloat(value);
      } else if (typeof value === 'number') {
        num = value;
      } else {
        num = 0;
      }
      
      if (isNaN(num)) return amount ? "0" : 0;
      
      if (amount) {
        return num.toLocaleString("en-IN");
      }
      return num % 1 !== 0 ? num.toFixed(2) : num;
    },
    
    formatPercentage(value) {
      if (value === undefined || value === null) return "0%";
      
      let num;
      if (typeof value === 'string') {
        num = parseFloat(value);
      } else if (typeof value === 'number') {
        num = value;
      } else {
        return "0%";
      }
      
      if (isNaN(num)) return "0%";
      return `${num.toFixed(2)}%`;
    },
    
    async fetchReport() {
      this.loading = true;
      const queryString = this.objectToQueryParams(this.form);
      
      try {
        const res = await this.$axios.get(`/get_addon_programme_fee_report?${queryString}`);
        this.reportData = res.data.data;
        
        // Ensure all numeric values are properly formatted
        if (this.reportData.fee) {
          this.reportData.fee = this.reportData.fee.map(item => ({
            ...item,
            fee_raised_count: Number(item.fee_raised_count) || 0,
            fee_raised_value: Number(item.fee_raised_value) || 0,
            fee_received_count: Number(item.fee_received_count) || 0,
            fee_received_value: Number(item.fee_received_value) || 0,
            received_percentage: Number(item.received_percentage) || 0
          }));
        }
      } catch (error) {
        const errorMessage = await this.$store.dispatch("throwError", error.response);
        this.$store.commit("showSnackbar", {
          message: errorMessage,
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    
    objectToQueryParams(obj) {
      const params = new URLSearchParams();
      
      Object.keys(obj).forEach((key) => {
        if (obj[key] !== undefined && obj[key] !== null && obj[key] !== "") {
          if (Array.isArray(obj[key]) && obj[key].length) {
            obj[key].forEach((v) => params.append(`${key}[]`, v));
          } else if (!Array.isArray(obj[key])) {
            params.append(key, obj[key]);
          }
        }
      });
      
      return params.toString();
    },
    
    async download() {
      this.downloadLoading = true;
      const queryString = this.objectToQueryParams(this.form);
      
      try {
        const res = await this.$axios.get(`/download_addon_programme_fee_report?${queryString}`, {
          responseType: "blob",
        });
        
        const blob = new Blob([res.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Addon_Programme_Fee_Report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.$store.commit("showSnackbar", {
          message: "Report downloaded successfully",
          color: "success",
        });
      } catch (error) {
        console.log(error);
        this.$store.commit("showSnackbar", {
          message: "Failed to download report",
          color: "error",
        });
      } finally {
        this.downloadLoading = false;
      }
    },
    async generateBatchList() {
      try {
        const response = await this.$axios.get("/get_year_batch");
        this.batches = response.data.data;
        // this.form.year_batch = this.batches[0];
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
.right-align-row {
  text-align: right;
}
.highlight-row {
  background-color: #f2f2f2 !important;
  font-weight: bold;
}
.text-right {
  text-align: right;
}
</style>